/*
 * Dehde 10x · search layer behaviour (bible surface "menu", notes 4-8). Loaded by snippets/search-bar.liquid
 * (header, predictive branch). Results, states and the option keyboard: assets/predictive-search.js.
 *
 * 1. Layer geometry: marks the header's .search-bar_dropdown-wrapper as .dehde-search-host (styles in
 *    dehde-search.css) and sets --dehde-search-top = bottom of the announcement strip (0 once scrolled away;
 *    the CSS forces 0 below 768px), so the fixed full-width layer covers header row 1.
 * 2. "Chiudi ricerca" and a click on the backdrop close the layer and return focus to the opener.
 * 3. Tab stays inside the open layer (wraps first ↔ last).
 * 4. Focus on ANY [data-dehde-search-field] (results page, 404) opens the header layer with its value.
 *    The field is a plain GET form, so it still works without JS.
 * 5. Results page, "Collezioni" tab (progressive enhancement — Shopify's /search has no collection results):
 *    asks the predictive organizer for up to 10 collections and, if any, shows the tab with the same rows as the
 *    layer (count printed only under 10). Hidden without JS or with 0 collections.
 *    Inactive tab counts come from the section render of that tab, so every number printed is Shopify's own.
 */
(function () {
  if (window.DehdeSearch) return;

  const surface = document.querySelector('.search-bar_dropdown-wrapper .dehde-search-bar');
  const host = surface ? surface.closest('.search-bar_dropdown-wrapper') : null;
  const component = host ? host.closest('dropdown-component') : null;
  const opener = component ? component.querySelector('.dropdown-component_opener') : null;
  const input = host ? host.querySelector('[data-dehde-search-input]') : null;
  const predictive = host ? host.querySelector('predictive-button') : null;

  const isOpen = () => !!host && host.classList.contains('dropdown--open');

  function place() {
    if (!host) return;
    const strip = document.querySelector('.announcement-bar-component');
    let top = 0;
    if (strip && window.matchMedia('(min-width: 768px)').matches) {
      top = Math.max(0, Math.round(strip.getBoundingClientRect().bottom));
    }
    host.style.setProperty('--dehde-search-top', `${top}px`);
  }

  function close() {
    if (!component || !isOpen()) return;
    component.close();
    if (opener) opener.focus();
  }

  function open(value) {
    if (!component) return;
    if (!isOpen()) component.open();
    if (input && typeof value === 'string') {
      input.value = value;
      if (predictive && typeof predictive.refresh === 'function') predictive.refresh();
    }
    if (input) input.focus();
  }

  if (host) {
    host.classList.add('dehde-search-host');
    place();

    // open / close of the Urban dropdown = the .dropdown--open class on the wrapper
    new MutationObserver(() => {
      if (isOpen()) {
        place();
        if (predictive && typeof predictive.refresh === 'function') predictive.refresh();
      }
    }).observe(host, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('scroll', () => { if (isOpen()) place(); }, { passive: true });
    window.addEventListener('resize', () => { if (isOpen()) place(); });

    host.addEventListener('click', (event) => {
      if (event.target.closest('[data-dehde-search-close]') || event.target.closest('[data-dehde-search-backdrop]')) {
        event.preventDefault();
        close();
      }
    });

    // Tab trap: only the visible focusables of the open layer
    host.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !isOpen()) return;
      const list = Array.from(host.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------- 4. any [data-dehde-search-field] opens the layer ----------
     A mouse/touch focus is followed by a click; opening on focus would let that click (outside the dropdown)
     close the layer again (Urban closes on body click), so a pointer focus opens after the click has finished
     bubbling (the body listener Urban adds on open would otherwise still receive this same click). */
  let pointerField = null;
  document.addEventListener('pointerdown', (event) => {
    pointerField = event.target.closest ? event.target.closest('[data-dehde-search-field]') : null;
  }, true);

  document.addEventListener('focusin', (event) => {
    const field = event.target.closest ? event.target.closest('[data-dehde-search-field]') : null;
    if (!field || !host) return;
    if (pointerField === field) {
      field.addEventListener('click', () => { setTimeout(() => open(field.value), 0); }, { once: true });
    } else {
      open(field.value);
    }
    pointerField = null;
  });

  /* ---------- 5. results page: "Collezioni" tab + inactive tab counts ---------- */
  function initResults() {
    const root = document.querySelector('[data-dehde-results]');
    if (!root) return;
    const q = root.dataset.terms;
    if (!q) return;
    const Search = window.customElements.get('predictive-button');
    const bold = (scope) => {
      if (!Search || !Search.highlight) return;
      const words = Search.words(q);
      scope.querySelectorAll('[data-dehde-hl]').forEach((el) => Search.highlight(el, words));
    };
    bold(root);

    // "Nessun risultato" is about products: if another tab finds something, the heading says so
    const found = () => {
      if (!root.hasAttribute('data-empty')) return;
      const h1 = root.querySelector('[data-dehde-results-title]');
      if (h1 && root.dataset.titleFound) h1.textContent = root.dataset.titleFound;
      const intro = root.querySelector('[data-dehde-empty-intro]');
      if (intro) intro.hidden = true;
      root.removeAttribute('data-empty');
    };

    // counts of the other server tabs, from the section render of that tab (exact results_count)
    root.querySelectorAll('[data-dehde-tab-count-url]').forEach((tab) => {
      fetch(tab.dataset.dehdeTabCountUrl)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const src = doc.querySelector('[data-dehde-results]');
          const n = src ? src.dataset.count : '';
          if (n === '' || n === undefined) return;
          const out = tab.querySelector('[data-dehde-tab-count]');
          if (out) out.textContent = n;
          // typeless /search?q= (hand-typed or shared): the server drew the Prodotti pane from mixed results (no
          // count, pages counted in "Visti X di Y", "Carica altri" without type). Swap in the typed render's pane.
          if (tab.hasAttribute('data-dehde-products-tab')) {
            const paneSel = '[data-dehde-pane]:not([data-dehde-coll-pane])';
            const livePane = root.querySelector(paneSel);
            const typedPane = src.querySelector(paneSel);
            if (livePane && typedPane) {
              const pane = document.importNode(typedPane, true);
              livePane.replaceWith(pane);
              bold(pane);
            }
          }
          if (parseInt(n, 10) > 0) found();
        })
        .catch(() => {});
    });

    const collTab = root.querySelector('[data-dehde-coll-tab]');
    const collPane = root.querySelector('[data-dehde-coll-pane]');
    const collList = collPane ? collPane.querySelector('[data-dehde-coll-list]') : null;
    if (!collTab || !collPane || !collList) return;

    // the same rows as the layer (sections/predictive-organizer.liquid): type tag and product count server-side
    const base = (window.routes && routes.predictive_search_url) || '/search/suggest';
    fetch(`${base}?q=${encodeURIComponent(q)}&section_id=predictive-organizer&resources[type]=collection&resources[limit]=10`)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const rows = Array.from(doc.querySelectorAll('.dehde-search_row'));
        if (!rows.length) return;
        rows.forEach((source) => {
          const row = document.importNode(source, true);
          // a plain link here: the listbox roles and ids belong to the header layer
          ['id', 'role', 'aria-selected', 'data-dehde-opt'].forEach((a) => row.removeAttribute(a));
          const title = row.querySelector('[data-dehde-hl]');
          if (title && Search && Search.highlight) Search.highlight(title, Search.words(q));
          collList.appendChild(row);
        });
        const count = collTab.querySelector('[data-dehde-tab-count]');
        if (count && rows.length < 10) count.textContent = rows.length;
        collTab.closest('li').hidden = false;
        found();

        collTab.addEventListener('click', (event) => {
          event.preventDefault();
          root.querySelectorAll('[data-dehde-pane]').forEach((p) => { p.hidden = p !== collPane; });
          root.querySelectorAll('[data-dehde-tab]').forEach((t) => {
            if (t === collTab) t.setAttribute('aria-current', 'page');
            else t.removeAttribute('aria-current');
          });
        });
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initResults);
  else initResults();

  window.DehdeSearch = { open, close };
})();
